<template>
  <div
    :class="[
      'rounded-lg text-center flex flex-col relative h-full overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-xl border',
      isOlimpiadasCourse ? 'bg-white border-amber-300/60' : cardStyle === 'white' ? 'bg-white border-[#004DBC]/30' : 'border-white/20'
    ]"
    v-if="userPermission !== 'ROLE_STUDENT' || course.hidden !== false"
  >
    <div
      v-if="course.state === 'completed'"
      class="absolute right-3 top-3 z-10"
      :class="(usesColoredBackground || isOlimpiadasCourse) ? 'text-white' : 'text-blue-500'"
    >
      <Icon icon="iconoir:verified-badge" height="25" />
    </div>
    <div
      class="course-background rounded-t-lg flex flex-col items-center pt-4"
      :class="[
        cardStyle === 'white' && !isOlimpiadasCourse ? 'course-background--white bg-white' : '',
        isOlimpiadasCourse ? 'course-background--olimpiadas text-white' : '',
        !isOlimpiadasCourse ? cardTextClass : ''
      ]"
      :style="courseBackgroundStyleInline"
    >
      <div class="w-full flex items-start justify-between px-4">
        <span
          v-if="course.isPremium"
          class="items-center rounded-full px-2.5 py-1 text-xs font-medium text-white bg-[#F59E0B]"
          >Premium</span
        >
        <span v-else class="h-6"></span>

        <div class="custom-switch" v-if="userPermission !== 'ROLE_STUDENT'">
          <button
            type="button"
            id="customSwitch"
            class="custom-switch-button"
            :class="{ active: isChecked }"
            @click="toggleSwitch(course.id)"
            v-b-tooltip.hover.custom-tooltip
            title="Visible para estudiantes (encendido = visible)"
          >
            <span class="custom-switch-inner"></span>
            <span class="custom-switch-switch"></span>
          </button>
        </div>
      </div>

      <div class="course-image mt-2" :class="iconBgClass" :style="{ 'background-image': 'url(' + backgroundImage + ')' }"></div>

      <h2 class="text-lg md:text-xl font-semibold leading-tight mt-4 line-clamp-2">
        {{ course.title }}
      </h2>

      <p class="font-medium leading-tight line-clamp-2 text-sm md:text-[15px] mt-1 px-4" :class="isOlimpiadasCard || isOlimpiadasCourse ? (isOlimpiadasCourse ? 'text-white/95' : 'text-[#1f2937]') : subtitleClass">
        {{ course.description }}
      </p>

      <!-- Barra de progreso: real si hay dato, esqueleto si está cargando (solo en cursos matriculados) -->
      <div v-if="course.enrolled" class="w-full px-4 mt-3 mb-1">
        <div class="w-full rounded-full h-2" :class="isOlimpiadasCourse ? 'bg-white/30' : progressTrackClass">
          <div
            v-if="course.progressPercentage !== null && course.progressPercentage !== undefined"
            class="rounded-full h-2 transition-all duration-300"
            :class="isOlimpiadasCourse ? 'bg-white' : progressFillClass"
            :style="{ width: course.progressPercentage + '%' }"
          ></div>
          <div v-else class="w-3/4">
            <!-- <b-skeleton animation="wave" width="100%" height="8px"></b-skeleton> -->
            <!-- <b-skeleton animation="fade" width="100%" height="8px"></b-skeleton> -->
          </div>
        </div>
      </div>
    </div>

    <div class="flex" :class="isOlimpiadasCourse ? 'bg-white' : ''">
      <button
        :class="[
          'flex-1 w-full',
          'mob-btn',
          'course-cta',
          'rounded-t-none rounded-b-lg',
          isOlimpiadasCourse ? 'bg-white text-neutral-800 hover:bg-neutral-100 border-t border-amber-200' : ctaClass,
        ]"
        @click="goToCourse()"
      >
        {{ stateText }}
      </button>      
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue2";
import { BFormCheckbox } from "bootstrap-vue";
import { enableShadow, disableShadow } from "@/services/courses.service";
import { changeOlympicShadowMode } from "@/services/olympics.service";

export default {
  name: "CourseCard",
  components: {
    Icon,
    BFormCheckbox,
  },
  props: {
    course: {
      type: Object,
      required: true,
      default: () => ({
        title: "",
        description: "",
        image: "/images/courses/course-1.png",
        state: "unstarted",
        progressPercentage: null,
      }),
    },
    index: {
      type: Number,
      default: 0,
    },
    /** 'gradient' | 'white' | 'primary-blues' - from parent (user preference) */
    cardStyle: {
      type: String,
      default: 'white',
      validator: (v) => ['gradient', 'white', 'primary-blues'].includes(v),
    },
  },
  data() {
    return {
      stateClass: "mob-btn--secondary",
      stateText: "Suscribirse",
      isChecked: this.course.hidden === true,
      userPermission: "",
    };
  },
  computed: {
    usesColoredBackground() {
      return this.cardStyle === 'gradient' || this.cardStyle === 'primary-blues';
    },
    isOlimpiadasCourse() {
      return this.course.isOlympic === true || this.course.id === 636;
    },
    /** Inline style for the card top section (gradient or primary blues); empty for white.
     * Gradients match the reference: horizontal two-tone (lighter left → darker right). */
    courseBackgroundStyleInline() {
      if (this.isOlimpiadasCourse) {
        return { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)' };
      }
      if (this.cardStyle === 'white') return {};
      const primary = "#004DBC";
      const accent = "#0078FF";
      const blue = "#598dff";
      if (this.cardStyle === 'primary-blues') {
        const azulesGradients = [
          `linear-gradient(90deg, ${primary} 0%, ${accent} 50%, ${blue} 100%)`,
          `linear-gradient(90deg, #3182CE 0%, #2c5282 100%)`,
          `linear-gradient(90deg, #4A90D9 0%, #1e4976 100%)`,
          `linear-gradient(90deg, #5B8FD4 0%, #2F855A 100%)`,
          `linear-gradient(90deg, #3182CE 0%, #38A169 100%)`,
          `linear-gradient(90deg, #38A169 0%, #276749 100%)`,
          `linear-gradient(90deg, #553C9A 0%, #44337A 100%)`,
          `linear-gradient(90deg, #6B46C1 0%, #553C9A 100%)`,
          `linear-gradient(90deg, #5B8FD4 0%, #6B46C1 100%)`,
        ];
        const i = (this.index ?? 0) % azulesGradients.length;
        return { background: azulesGradients[i] };
      }
      // Two-tone horizontal gradients like the reference image: blue→darker blue, red→darker red, blue→green, orange→yellow
      const gradients = [
        `linear-gradient(90deg, #4A90D9 0%, #1e4976 100%)`,   // blue → darker blue
        `linear-gradient(90deg, #5B9BD5 0%, #2c5282 100%)`,   // light blue → dark blue
        `linear-gradient(90deg, #3182CE 0%, #2c5282 100%)`,    // blue → deep blue
        `linear-gradient(90deg, #E53E3E 0%, #742a2a 100%)`,   // red → darker red/brown
        `linear-gradient(90deg, #DD6B20 0%, #C05621 100%)`,   // red-orange → darker
        `linear-gradient(90deg, #3182CE 0%, #2F855A 100%)`,   // blue → green
        `linear-gradient(90deg, #38A169 0%, #276749 100%)`,   // green → darker green
        `linear-gradient(90deg, #ED8936 0%, #ECC94B 100%)`,   // orange → yellow
        `linear-gradient(90deg, #DD6B20 0%, #F6E05E 100%)`,   // orange → yellow
      ];
      const i = (this.index ?? 0) % gradients.length;
      return { background: gradients[i] };
    },
    isLightCard() {
      return (
        !this.course.isPremium &&
        (this.course.isFree === true ||
          this.course.free === true ||
          this.course.isIntro === true ||
          this.course.price === 0)
      );
    },
    cardTextClass() {
      return this.usesColoredBackground ? "text-white" : "text-gray-900";
    },
    subtitleClass() {
      return this.usesColoredBackground ? "text-white/85" : "text-gray-500";
    },
    iconBgClass() {
      if (this.isOlimpiadasCourse) return "bg-white/20";
      return this.usesColoredBackground ? "bg-white/15" : "bg-blue-50/80";
    },
    progressTrackClass() {
      return this.usesColoredBackground ? "bg-white/30" : "bg-gray-200";
    },
    progressFillClass() {
      return this.usesColoredBackground ? "bg-white" : "bg-blue-600";
    },
    ctaClass() {
      if (this.usesColoredBackground) {
        return "bg-white text-black hover:bg-neutral-100 hover:shadow-md border-t border-neutral-200";
      }
      return "course-cta--mobius bg-[#004DBC] text-white hover:bg-[#003d99] hover:shadow-md";
    },
    shouldApplyDarkBackground() {
      return (
        this.userPermission !== "ROLE_STUDENT" && this.course.hidden === false
      );
    },
    backgroundImage() {
      return this.course.icon
        ? this.course.icon
        : "/images/courses/course-1.png";
    },
    isPremiumUser() {
      return this.$store.state.auth.isPremium;
    },
    isOlimpiadasCard() {
      const t = (this.course.title || '').toLowerCase();
      return t.includes('olimpiada') || t.includes('olímpi');
    },
  },
  methods: {
    goToCourse() {
      if (this.course.isOlympic || this.isOlimpiadasCourse) {
        const moduleId = this.course.id;
        const query = this.course.slug ? { slug: this.course.slug } : {};
        this.$router.push({ path: `/course/${moduleId}/phases`, query });
        return;
      }
      this.$router.push(`/course/${this.course.id}`);
    },
    setCourseState() {      
      if (this.course.enrolled) {
        if (this.course.finished) {
          this.stateText = "Completado";
          return;
        }
        this.stateText = "Retomar";
      } else {
        this.stateText = "Ver Curso";
      }
      
    },
    async toggleSwitch(id) {
      this.isChecked = !this.isChecked;
      let loader = this.$loading.show({});
      try {
        if (this.course.isOlympic && this.course.olympicId) {
          await changeOlympicShadowMode(this.course.olympicId);
        } else if (this.isChecked) {
          await enableShadow(id);
        } else {
          await disableShadow(id);
        }
        this.$router.go();
      } catch (error) {
        console.error(error);
        this.isChecked = !this.isChecked;
      } finally {
        loader.hide();
      }
    },
  },
  mounted() {
    this.userPermission = localStorage.getItem("role");
    this.setCourseState();
  },
};
</script>

<style scoped>
.custom-switch-button.active .custom-switch-inner {
  background-color: #0078FF;
}

.course-background {
  height: 240px;
  position: relative;
}

.course-image {
  width: 100px;
  height: 100px;
  background-size: cover;
  top: 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mob-btn {
  background-image: none !important;
  height: 60px;
}

.course-cta {
  box-shadow: none !important;
}

.course-cta--mobius {
  background-color: #004DBC !important;
  color: white !important;
}
.course-cta--mobius:hover {
  background-color: #003d99 !important;
}

button {
  border-width: 0;
}
</style>
